import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar
} from '@mui/material';
import {
  Person,
  People,
  Business,
  LocationOn,
  LocalPhone,
  Phone,
  PhoneAndroid,
  Email,
  Cake,
  AttachMoney,
  CreditScore
} from '@mui/icons-material';
import { Button } from '@mui/material';
import PrequalAssistant from './PrequalAssistant';

const PropertyInsightsTable = ({
  propertyData,
  loading,
  error,
  ownersWithPhones,
  skipTracingLoading,
  skipTracingStatus,
  // BatchData Props
  premiumData,
  premiumDataLoading,
  premiumDataError,
  fetchPremiumData
}) => {
  // HOOKS MUST BE AT THE TOP LEVEL (Before any early returns)
  const [openPrequal, setOpenPrequal] = React.useState(false);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
        <Typography variant="body2" ml={2}>
          Loading property insights...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!propertyData) {
    return (
      <Box p={3}>
        <Typography variant="body2" color="text.secondary">
          Click on a location and use the insights button to get property data.
        </Typography>
      </Box>
    );
  }

  // Extract homeowner information from API structure
  const getHomeownerInfo = () => {
    const ownership = propertyData.ownership;

    if (!ownership) return null;

    const currentOwners = ownership.currentOwners;
    const mailingInfo = ownership.currentOwnerMailingInfo;

    if (!currentOwners) return null;

    return {
      ownerNames: currentOwners.ownerNames || [],
      occupancyCode: currentOwners.occupancyCode,
      mailingAddress: mailingInfo?.mailingAddress
    };
  };

  const homeownerInfo = getHomeownerInfo();

  // Determine if property is owner-occupied or tenant-occupied
  const getOccupancyStatus = (code) => {
    switch (code) {
      case 'O': return 'Owner-Occupied';
      case 'T': return 'Tenant-Occupied';
      case 'V': return 'Vacant';
      default: return 'Unknown';
    }
  };

  // Check if mailing address matches property address (indicates owner-occupancy)
  const isOwnerOccupied = () => {
    if (!homeownerInfo?.mailingAddress || !propertyData?.propertyAddress) return null;

    const mailingAddress = homeownerInfo.mailingAddress.streetAddress;
    const propertyAddress = propertyData.propertyAddress.streetAddress;

    return mailingAddress === propertyAddress;
  };

  const occupancyStatus = getOccupancyStatus(homeownerInfo?.occupancyCode);
  const ownerOccupied = isOwnerOccupied();

  return (
    <Box
      sx={{
        overflowY: 'auto',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: '6px',
          height: '6px'
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
        },
        '&::-webkit-scrollbar-corner': {
          background: 'transparent'
        }
      }}
      p={1}
    >
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Header Area */}
        <Box display="flex" alignItems="center">
          <AttachMoney sx={{ mr: 1, color: '#4caf50' }} />
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Property Insights
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<CreditScore />}
          onClick={() => setOpenPrequal(true)}
          sx={{ fontWeight: 600, textTransform: 'none' }}
        >
          Run Credit Check
        </Button>
      </Box>

      {/* Prequalification Assistant Modal */}
      <PrequalAssistant
        open={openPrequal}
        onClose={() => setOpenPrequal(false)}
        candidate={propertyData}
      />

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableBody>
            {/* Property Address */}
            <TableRow>
              <TableCell>
                <Box display="flex" alignItems="center">
                  <LocationOn sx={{ mr: 1, fontSize: 16 }} />
                  Property Address
                </Box>
              </TableCell>
              <TableCell>
                {propertyData.propertyAddress?.streetAddress || propertyData.streetAddress || 'N/A'}
              </TableCell>
            </TableRow>


            {/* Homeowner Information - Most Important for Sales */}
            {homeownerInfo && (
              <>
                <TableRow>
                  <TableCell>Current Homeowners</TableCell>
                  <TableCell>
                    {homeownerInfo.ownerNames.length > 0 ? (
                      <>
                        <List dense sx={{ p: 0 }}>
                          {homeownerInfo.ownerNames
                            .filter(owner => owner.fullName) // Only show owners with names
                            .map((owner, index) => (
                              <ListItem key={index} sx={{ p: 0, minHeight: 'auto' }}>
                                <ListItemAvatar sx={{ minWidth: 32 }}>
                                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                    {owner.isCorporate ? <Business /> : <Person />}
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={owner.fullName}
                                  secondary={
                                    <React.Fragment>
                                      {owner.isCorporate ? 'Corporate Owner' : 'Individual Owner'}

                                      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {/* Age Badge */}
                                        {owner.age && (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <Chip
                                              icon={<Cake sx={{ fontSize: '14px !important' }} />}
                                              label={`${owner.age} years old`}
                                              size="small"
                                              sx={{ height: 20, fontSize: '0.7rem', backgroundColor: 'rgba(255, 64, 129, 0.1)', color: '#ff4081' }}
                                            />
                                          </Box>
                                        )}

                                        {/* Phone Numbers List */}
                                        {(owner.phones && owner.phones.length > 0) || owner.phoneNumber ? (
                                          <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                              Phone Numbers
                                            </Typography>
                                            {(owner.phones && owner.phones.length > 0 ? owner.phones : [{ number: owner.phoneNumber }]).map((phone, idx) => (
                                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <LocalPhone sx={{ fontSize: 14, color: '#4caf50' }} />
                                                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                  {phone.number}
                                                </Typography>
                                                {phone.type && (
                                                  <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                                                    • {phone.type} {phone.carrier ? `(${phone.carrier})` : ''}
                                                  </Typography>
                                                )}
                                              </Box>
                                            ))}
                                          </Box>
                                        ) : null}

                                        {/* Emails List */}
                                        {(owner.emails && owner.emails.length > 0) || owner.email ? (
                                          <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                              Emails
                                            </Typography>
                                            {(owner.emails && owner.emails.length > 0 ? owner.emails : [owner.email]).map((email, idx) => (
                                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Email sx={{ fontSize: 14, color: '#2196f3' }} />
                                                <Typography
                                                  component="a"
                                                  href={`mailto:${email}`}
                                                  variant="body2"
                                                  sx={{
                                                    color: '#2196f3',
                                                    textDecoration: 'none',
                                                    fontSize: '0.8rem',
                                                    '&:hover': { textDecoration: 'underline' }
                                                  }}
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  {email}
                                                </Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        ) : null}

                                        {/* Relatives List */}
                                        {owner.relatives && owner.relatives.length > 0 && (
                                          <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                              Possible Relatives
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#aaa', lineHeight: 1.4, display: 'block' }}>
                                              {owner.relatives.slice(0, 5).join(' • ')}
                                              {owner.relatives.length > 5 && (
                                                <Typography component="span" variant="caption" sx={{ color: '#666', ml: 0.5 }}>
                                                  (+{owner.relatives.length - 5} more)
                                                </Typography>
                                              )}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    </React.Fragment>
                                  }
                                  primaryTypographyProps={{ variant: 'body1', fontWeight: 600, color: '#fff' }}
                                  secondaryTypographyProps={{ component: 'div' }}
                                />
                              </ListItem>
                            ))}
                        </List>
                        {homeownerInfo.ownerNames.filter(owner => owner.fullName).length > 2 && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            ⚠️ {homeownerInfo.ownerNames.filter(owner => owner.fullName).length} owners found.
                            Add Prospect form will show first 2 owners only.
                          </Typography>
                        )}

                      </>
                    ) : 'No homeowner information available'}
                  </TableCell>
                </TableRow>

                {/* Estimated Value Row - Check for various common keys */}
                {(propertyData.estimatedValue || propertyData.taxAssessment?.totalAssessedValue || propertyData.salePrice) && (
                  <TableRow>
                    <TableCell sx={{ verticalAlign: 'middle' }}>
                      <Box display="flex" alignItems="center">
                        <AttachMoney sx={{ mr: 1, fontSize: 16, color: '#ff9800' }} />
                        Est. Value
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          propertyData.estimatedValue
                            ? `$${propertyData.estimatedValue.toLocaleString()}`
                            : propertyData.taxAssessment?.totalAssessedValue
                              ? `$${propertyData.taxAssessment.totalAssessedValue.toLocaleString()}`
                              : `$${propertyData.salePrice?.toLocaleString()}`
                        }
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          color: '#4caf50',
                          border: '1px solid rgba(76, 175, 80, 0.3)',
                          fontWeight: 600,
                          height: 24
                        }}
                      />
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.7rem' }}>
                        {propertyData.estimatedValue
                          ? 'Estimated Market Value'
                          : propertyData.taxAssessment?.totalAssessedValue
                            ? 'Tax Assessed Value'
                            : 'Last Sale Price'
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                <TableRow>
                  <TableCell>Occupancy Status</TableCell>
                  <TableCell>
                    <Chip
                      label={occupancyStatus}
                      size="small"
                      color={
                        occupancyStatus === 'Owner-Occupied' ? 'success' :
                          occupancyStatus === 'Tenant-Occupied' ? 'warning' :
                            'default'
                      }
                    />
                    {ownerOccupied !== null && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                        {ownerOccupied ?
                          '✓ Mailing address matches property address' :
                          '⚠ Mailing address differs from property address'
                        }
                      </Typography>
                    )}

                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Premium Data Section - BatchData Integration */}
      <Box mt={2} sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
        {!premiumData && !premiumDataLoading && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<Business />}
            onClick={() => {
              const street = propertyData.propertyAddress?.streetAddress || propertyData.streetAddress;
              const city = propertyData.propertyAddress?.city || propertyData.city;
              const state = propertyData.propertyAddress?.state || propertyData.state;
              const zip = propertyData.propertyAddress?.zipCode || propertyData.zipCode;

              fetchPremiumData({
                street,
                city,
                state,
                zip
              });
            }}
            sx={{ fontWeight: 600, textTransform: 'none', width: '100%' }}
          >
            Get Emails & Phone Numbers
          </Button>
        )}

        {premiumDataLoading && (
          <Box display="flex" alignItems="center" py={1}>
            <CircularProgress size={18} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Fetching premium data...
            </Typography>
          </Box>
        )}

        {premiumDataError && (
          <Alert severity="error" sx={{ mt: 1 }}>{premiumDataError}</Alert>
        )}

        {premiumData && (
          <Box>
            {/* Value & Mortgage Cards */}
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              {/* <Paper
                elevation={2}
                sx={{
                  p: 1.5,
                  flex: '1 1 45%',
                  minWidth: 120,
                  background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
                  border: '1px solid rgba(76, 175, 80, 0.3)'
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block">
                  Est. Value
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  ${premiumData.details?.valuation?.estimatedValue?.toLocaleString() || 'N/A'}
                </Typography>
              </Paper> */}

              {/* <Paper
                elevation={2}
                sx={{
                  p: 1.5,
                  flex: '1 1 45%',
                  minWidth: 120,
                  background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)',
                  border: '1px solid rgba(33, 150, 243, 0.3)'
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block">
                  Mortgage
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  ${
                    (premiumData.details?.mortgage?.firstMortgageAmount ||
                      premiumData.details?.propertyOwnerProfile?.mortgagesTotalBalance ||
                      0).toLocaleString() === '0' ? 'N/A' :
                      (premiumData.details?.mortgage?.firstMortgageAmount ||
                        premiumData.details?.propertyOwnerProfile?.mortgagesTotalBalance
                      ).toLocaleString()
                  }
                </Typography>
              </Paper> */}
            </Box>

            {/* Verified Contacts */}
            {premiumData.contacts && premiumData.contacts.length > 0 && (
              <Box mt={2}>
                {/* Check if we have at least one valid contact to show before rendering header */}
                {premiumData.contacts.some(c => (c.name?.first || c.name?.last) && c.phoneNumbers?.length > 0) && (
                  <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, display: 'block', letterSpacing: 1, fontWeight: 700 }}>
                    VERIFIED CONTACTS
                  </Typography>
                )}

                {premiumData.contacts.map((contact, i) => {
                  // Skip invalid contacts (must have name or phones)
                  if (!contact.name?.first && !contact.name?.last && (!contact.phoneNumbers || contact.phoneNumbers.length === 0)) {
                    return null;
                  }

                  return (
                    <Paper
                      key={i}
                      elevation={2}
                      sx={{
                        p: 2,
                        mb: 2,
                        background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 2
                      }}
                    >
                      {/* Contact Header */}
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar sx={{ width: 40, height: 40, mr: 1.5, bgcolor: 'primary.main', fontWeight: 'bold' }}>
                          {contact.name?.first ? contact.name.first[0] : <Person />}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                            {contact.name?.full || `${contact.name?.first || ''} ${contact.name?.last || ''}`}
                          </Typography>
                          {contact.age && (
                            <Chip
                              label={`${contact.age} years`}
                              size="small"
                              sx={{ mt: 0.5, height: 18, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.1)' }}
                            />
                          )}
                        </Box>
                      </Box>

                      {/* Emails Section - Moved to Top per User Request */}
                      {contact.emails?.length > 0 && (
                        <Box mb={2}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                            EMAILS
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {contact.emails.map((e, idx) => (
                              <Chip
                                key={idx}
                                icon={<Email sx={{ fontSize: '14px !important' }} />}
                                label={e.email}
                                size="small"
                                variant="outlined"
                                color={e.tested ? "info" : "default"}
                                sx={{
                                  borderRadius: 1,
                                  bgcolor: e.tested ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
                                  border: e.tested ? '1px solid rgba(33, 150, 243, 0.3)' : '1px solid rgba(255,255,255,0.1)'
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Phone Numbers Section */}
                      {contact.phoneNumbers?.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                            PHONE NUMBERS
                          </Typography>
                          <Box display="flex" flexDirection="column" gap={1}>
                            {contact.phoneNumbers.map((p, idx) => (
                              <Box
                                key={idx}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  p: 0.75,
                                  borderRadius: 1,
                                  bgcolor: 'rgba(76, 175, 80, 0.08)',
                                  border: '1px solid rgba(76, 175, 80, 0.2)'
                                }}
                              >
                                {p.type === 'Mobile' ?
                                  <PhoneAndroid sx={{ fontSize: 16, color: '#4caf50', mr: 1.5, ml: 0.5 }} /> :
                                  <Phone sx={{ fontSize: 16, color: '#4caf50', mr: 1.5, ml: 0.5 }} />
                                }
                                <Box>
                                  <Typography variant="body2" fontWeight="600" color="text.primary" fontFamily="monospace">
                                    {p.number.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>
                                    {p.type || 'Unknown'}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PropertyInsightsTable; 